import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';
import { storableError } from '../../util/errors';
import { parse } from '../../util/urlHelpers';
import { getAllTransitionsForEveryProcess } from '../../transactions/transaction';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { transitions as purchaseTransitions } from '../../transactions/transactionProcessPurchase';
import { transitions as bookingTransitions } from '../../transactions/transactionProcessBooking';
import { transitions as inquiryTransitions } from '../../transactions/transactionProcessInquiry';

const sortedTransactions = txs =>
  reverse(
    sortBy(txs, tx => {
      return tx.attributes ? tx.attributes.lastTransitionedAt : null;
    })
  );

export const ACTIVE_TRANSITIONS = [
  // bookingTransitions.REQUEST_PAYMENT,
  bookingTransitions.INQUIRE,
  // bookingTransitions.REQUEST_PAYMENT_AFTER_INQUIRY,
  bookingTransitions.CONFIRM_PAYMENT,
  // purchaseTransitions.REQUEST_PAYMENT intentionally omitted: Buy It Now
  // creates a pending-payment tx the moment the buyer clicks Continue (before
  // PayPal actually charges them). Showing it in either inbox would expose an
  // unconfirmed purchase to both parties. The tx becomes visible once it
  // transitions to confirm-payment (purchase tab) or expire-payment (completed).
  purchaseTransitions.INQUIRE,
  // REQUEST_PAYMENT_AFTER_INQUIRY surfaces in active (unlike REQUEST_PAYMENT
  // above) so a buyer who abandoned an offer-flow checkout can find the tx
  // again and finish paying. TransactionPage auto-redirects the customer to
  // PayPal whenever they open a PENDING_PAYMENT tx. The meta_isActive=true
  // filter on the active query keeps this scoped to offer-flow txs — direct
  // Buy-It-Now pending txs don't set that metadata and stay hidden.
  purchaseTransitions.REQUEST_PAYMENT_AFTER_INQUIRY,
  purchaseTransitions.BACK_TO_INQUIRY,
  purchaseTransitions.CONFIRM_PAYMENT,
  inquiryTransitions.INQUIRE_WITHOUT_PAYMENT,
];

export const CONFIRMED_PAYMENT_TRANSITIONS = [
  bookingTransitions.CONFIRM_PAYMENT,
  purchaseTransitions.CONFIRM_PAYMENT,
];

const ACTIVE_OFFER_TRANSITIONS = ACTIVE_TRANSITIONS.filter(
  transition => !CONFIRMED_PAYMENT_TRANSITIONS.includes(transition)
);

export const COMPLETED_TRANSITIONS = [
  bookingTransitions.EXPIRE_PAYMENT,
  bookingTransitions.INQUIRE,
  bookingTransitions.DECLINE,
  bookingTransitions.OPERATOR_DECLINE,
  bookingTransitions.EXPIRE,
  bookingTransitions.CANCEL,
  purchaseTransitions.EXPIRE_OFFER,
  // purchaseTransitions.EXPIRE_PAYMENT intentionally omitted: paired with
  // the ACTIVE_TRANSITIONS removal of REQUEST_PAYMENT above — if the pending
  // tx was never visible, its expiry shouldn't surface either.
  purchaseTransitions.AUTO_CANCEL,
  purchaseTransitions.CANCEL,
  purchaseTransitions.AUTO_CANCEL_FROM_DISPUTED,
  purchaseTransitions.CANCEL_FROM_DISPUTED,
  purchaseTransitions.DISPUTE,
  purchaseTransitions.OPERATOR_DISPUTE,
];

export const SUCCESSFUL_TRANSITIONS = [
  bookingTransitions.ACCEPT,
  bookingTransitions.OPERATOR_ACCEPT,
  bookingTransitions.COMPLETE,
  bookingTransitions.OPERATOR_COMPLETE,
  bookingTransitions.REVIEW_1_BY_PROVIDER,
  bookingTransitions.REVIEW_2_BY_PROVIDER,
  bookingTransitions.REVIEW_1_BY_CUSTOMER,
  bookingTransitions.REVIEW_2_BY_CUSTOMER,
  bookingTransitions.EXPIRE_CUSTOMER_REVIEW_PERIOD,
  bookingTransitions.EXPIRE_PROVIDER_REVIEW_PERIOD,
  bookingTransitions.EXPIRE_REVIEW_PERIOD,
  purchaseTransitions.MARK_DELIVERED,
  purchaseTransitions.OPERATOR_MARK_DELIVERED,
  purchaseTransitions.MARK_RECEIVED_FROM_PURCHASED,
  purchaseTransitions.MARK_RECEIVED,
  purchaseTransitions.AUTO_MARK_RECEIVED,
  purchaseTransitions.MARK_RECEIVED_FROM_DISPUTED,
  purchaseTransitions.AUTO_COMPLETE,
  purchaseTransitions.REVIEW_1_BY_PROVIDER,
  purchaseTransitions.REVIEW_2_BY_PROVIDER,
  purchaseTransitions.REVIEW_1_BY_CUSTOMER,
  purchaseTransitions.REVIEW_2_BY_CUSTOMER,
  purchaseTransitions.EXPIRE_CUSTOMER_REVIEW_PERIOD,
  purchaseTransitions.EXPIRE_PROVIDER_REVIEW_PERIOD,
  purchaseTransitions.EXPIRE_REVIEW_PERIOD,
];

// ================ Action types ================ //

export const FETCH_ORDERS_OR_SALES_REQUEST = 'app/InboxPage/FETCH_ORDERS_OR_SALES_REQUEST';
export const FETCH_ORDERS_OR_SALES_SUCCESS = 'app/InboxPage/FETCH_ORDERS_OR_SALES_SUCCESS';
export const FETCH_ORDERS_OR_SALES_ERROR = 'app/InboxPage/FETCH_ORDERS_OR_SALES_ERROR';
export const DELETE_TRANSACTION = 'app/InboxPage/DELETE_TRANSACTION';
export const LOAD_DELETED_TRANSACTIONS = 'app/InboxPage/LOAD_DELETED_TRANSACTIONS';

// ================ Reducer ================ //

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

const initialState = {
  fetchInProgress: false,
  fetchOrdersOrSalesError: null,
  pagination: null,
  transactionRefs: [],
  deletedTransactions: [],
};

export default function inboxPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_ORDERS_OR_SALES_REQUEST:
      return { ...state, fetchInProgress: true, fetchOrdersOrSalesError: null };
    case FETCH_ORDERS_OR_SALES_SUCCESS: {
      const transactions = sortedTransactions(payload.data.data);
      return {
        ...state,
        fetchInProgress: false,
        transactionRefs: entityRefs(transactions),
        pagination: payload.data.meta,
      };
    }
    case FETCH_ORDERS_OR_SALES_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchInProgress: false, fetchOrdersOrSalesError: payload };
    case DELETE_TRANSACTION: {
      const { transactionId, userId } = payload;
      const deletedTransactions = [...state.deletedTransactions, transactionId];
      if (typeof window !== 'undefined') {
        const key = `deletedTransactions_${userId || 'anonymous'}`;
        window.localStorage.setItem(key, JSON.stringify(deletedTransactions));
      }
      return { ...state, deletedTransactions };
    }
    case LOAD_DELETED_TRANSACTIONS: {
      return { ...state, deletedTransactions: payload };
    }

    default:
      return state;
  }
}

// ================ Action creators ================ //

const fetchOrdersOrSalesRequest = () => ({ type: FETCH_ORDERS_OR_SALES_REQUEST });
const fetchOrdersOrSalesSuccess = response => ({
  type: FETCH_ORDERS_OR_SALES_SUCCESS,
  payload: response,
});
const fetchOrdersOrSalesError = e => ({
  type: FETCH_ORDERS_OR_SALES_ERROR,
  error: true,
  payload: e,
});

export const deleteTransaction = (transactionId, userId) => ({
  type: DELETE_TRANSACTION,
  payload: { transactionId, userId },
});

export const loadDeletedTransactions = userId => dispatch => {
  if (typeof window !== 'undefined') {
    const key = `deletedTransactions_${userId || 'anonymous'}`;
    const deleted = JSON.parse(window.localStorage.getItem(key) || '[]');
    dispatch({ type: LOAD_DELETED_TRANSACTIONS, payload: deleted });
  }
};

// ================ Thunks ================ //

const INBOX_PAGE_SIZE = 10;
const MAX_ACTIVE_QUERY_PAGE_SIZE = 100;

const createActivePaginationMeta = (responses, page, perPage) => {
  const totalItems = responses.reduce(
    (total, response) => total + (response?.data?.meta?.totalItems || 0),
    0
  );

  return {
    page,
    perPage,
    totalItems,
    totalPages: Math.ceil(totalItems / perPage),
  };
};

const mergeActiveResponses = (responses, page, perPage) => {
  const offset = (page - 1) * perPage;
  const transactionsById = responses
    .flatMap(response => response?.data?.data || [])
    .reduce((txMap, tx) => {
      const id = tx?.id?.uuid || tx?.id;
      return id ? txMap.set(id, tx) : txMap;
    }, new Map());

  const transactions = sortedTransactions(Array.from(transactionsById.values())).slice(
    offset,
    offset + perPage
  );

  return {
    ...responses[0],
    data: {
      ...responses[0]?.data,
      data: transactions,
      meta: createActivePaginationMeta(responses, page, perPage),
    },
  };
};

const withActiveQueryPagination = (queryParams, page) => ({
  ...queryParams,
  page: 1,
  perPage: Math.min(page * INBOX_PAGE_SIZE, MAX_ACTIVE_QUERY_PAGE_SIZE),
});

export const loadData = (params, search) => (dispatch, getState, sdk) => {
  const { tab } = params;
  const { status = 'active', page: pageParam = 1 } = parse(search);
  const parsedPage = Number(pageParam);
  const page = Number.isFinite(parsedPage) && parsedPage > 0 ? parsedPage : 1;

  const meta_isActive =
    status === 'active' ? true : status === 'completed' ? false : undefined;

  const isOrders = tab === 'orders';
  const onlyFilter = isOrders ? 'order' : 'sale';

  let lastTransitions = [];
  if (status === 'active') lastTransitions = ACTIVE_TRANSITIONS;
  if (status === 'completed') lastTransitions = COMPLETED_TRANSITIONS;
  if (status === 'purchases' || status === 'sold') lastTransitions = SUCCESSFUL_TRANSITIONS;

  dispatch(fetchOrdersOrSalesRequest());

  const apiQueryParams = {
    only: onlyFilter,
    lastTransitions,
    ...(meta_isActive !== undefined && { meta_isActive }),
    include: [
      'listing',
      'provider',
      'provider.profileImage',
      'customer',
      'customer.profileImage',
      'booking',
    ],
    'fields.transaction': [
      'processName',
      'lastTransition',
      'lastTransitionedAt',
      'transitions',
      'payinTotal',
      'payoutTotal',
      'lineItems',
      'metadata',
    ],
    'fields.listing': ['title', 'availabilityPlan', 'publicData.listingType'],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'deleted', 'banned'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
    page,
    perPage: INBOX_PAGE_SIZE,
  };
  const confirmedPaymentQueryParams = { ...apiQueryParams };
  delete confirmedPaymentQueryParams.meta_isActive;

  // Transactions whose listing was deleted are flagged `listingDeleted` and
  // their `isActive` is set to false (see server/api/deactivateListingTransactions.js).
  // They belong in Completed regardless of their last transition, so query them
  // without the COMPLETED_TRANSITIONS / meta_isActive constraints and merge.
  const deletedListingQueryParams = { ...apiQueryParams };
  delete deletedListingQueryParams.meta_isActive;
  delete deletedListingQueryParams.lastTransitions;
  deletedListingQueryParams.meta_listingDeleted = true;

  const queryPromise =
    status === 'active'
      ? Promise.all([
          sdk.transactions.query(
            withActiveQueryPagination(
              {
                ...apiQueryParams,
                lastTransitions: ACTIVE_OFFER_TRANSITIONS,
                meta_isActive: true,
              },
              page
            )
          ),
          sdk.transactions.query(
            withActiveQueryPagination(
              {
                ...confirmedPaymentQueryParams,
                lastTransitions: CONFIRMED_PAYMENT_TRANSITIONS,
              },
              page
            )
          ),
        ]).then(responses => {
          responses.forEach(response => dispatch(addMarketplaceEntities(response)));
          return mergeActiveResponses(responses, page, INBOX_PAGE_SIZE);
        })
      : status === 'completed'
      ? Promise.all([
          sdk.transactions.query(withActiveQueryPagination(apiQueryParams, page)),
          sdk.transactions.query(withActiveQueryPagination(deletedListingQueryParams, page)),
        ]).then(responses => {
          responses.forEach(response => dispatch(addMarketplaceEntities(response)));
          return mergeActiveResponses(responses, page, INBOX_PAGE_SIZE);
        })
      : sdk.transactions.query(apiQueryParams);

  return queryPromise
    .then(response => {
      if (status !== 'active' && status !== 'completed') {
        dispatch(addMarketplaceEntities(response));
      }
      dispatch(fetchOrdersOrSalesSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(fetchOrdersOrSalesError(storableError(e)));
      throw e;
    });
};
